import "./main.sass"
import "../../../../aiaudio-extensions/sonn-skin/sonn.css"
import workersUrl from "@opendaw/studio-core/workers-main.js?worker&url"
import workletsUrl from "@opendaw/studio-core/processors.js?url"
import offlineEngineUrl from "@opendaw/studio-core/offline-engine.js?worker&url"
import {boot} from "@/boot"
import {initializeColors} from "@opendaw/studio-enums"
import {Browser} from "@opendaw/lib-dom"
import {isDefined, UUID} from "@opendaw/lib-std"
import {wireChatSidebar} from "../../../../aiaudio-extensions/chat-sidebar/wireUp.ts"
import {makeStudioContext} from "../../../../aiaudio-extensions/agent-bridge/openDawAdapter.ts"

if (Browser.isMobile()) {
    document.body.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:100vh;padding:2em;text-align:center;font-family:system-ui;color:#ccc;background:#1a1a1a">
        <div><h1>openDAW</h1><p>openDAW requires a desktop browser.<br>Please visit on a computer.</p></div>
    </div>`
} else if (window.crossOriginIsolated) {
    // Read Supabase callback from URL hash (OAuth redirect or email/password redirect)
    const urlHash = new URLSearchParams(window.location.hash.slice(1))
    const urlToken = urlHash.get('access_token')
    if (urlToken) {
        localStorage.setItem('sonn_jwt', urlToken)
        const refreshToken = urlHash.get('refresh_token')
        if (refreshToken) localStorage.setItem('sonn_refresh_token', refreshToken)
        const expiresIn = Number(urlHash.get('expires_in') ?? 3600)
        localStorage.setItem('sonn_jwt_expires_at', String(Date.now() + expiresIn * 1000))
        history.replaceState(null, '', window.location.pathname + window.location.search)
    }

    // JWT priority: URL hash (just stored) > localStorage cache > dev env stub
    const jwt = localStorage.getItem('sonn_jwt') ?? import.meta.env.VITE_DEV_TEST_JWT ?? ''

    // Production auth gate: only redirect when VITE_PLATFORM_URL is explicitly configured
    const platformUrl = import.meta.env.VITE_PLATFORM_URL ?? ''
    if (!jwt && platformUrl) {
        window.location.href = `${platformUrl}/login`
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? ''
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''
    const REFRESH_MARGIN_MS = 60_000

    async function refreshJwt(): Promise<void> {
        const refreshToken = localStorage.getItem('sonn_refresh_token')
        if (!refreshToken || !supabaseUrl || !supabaseAnonKey) return
        const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': supabaseAnonKey },
            body: JSON.stringify({ refresh_token: refreshToken }),
        })
        if (!res.ok) return
        const data = await res.json() as Record<string, unknown>
        if (typeof data.access_token !== 'string') return
        localStorage.setItem('sonn_jwt', data.access_token)
        if (typeof data.refresh_token === 'string') {
            localStorage.setItem('sonn_refresh_token', data.refresh_token)
        }
        const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : 3600
        localStorage.setItem('sonn_jwt_expires_at', String(Date.now() + expiresIn * 1000))
    }

    function getJwt(): Promise<string> {
        const expiresAt = Number(localStorage.getItem('sonn_jwt_expires_at') ?? 0)
        const needsRefresh = expiresAt > 0 && Date.now() > expiresAt - REFRESH_MARGIN_MS
        const base = needsRefresh ? refreshJwt() : Promise.resolve()
        return base.then(() => localStorage.getItem('sonn_jwt') ?? import.meta.env.VITE_DEV_TEST_JWT ?? '')
    }

    const now = Date.now()
    initializeColors(document.documentElement)
    boot({
        workersUrl,
        workletsUrl,
        offlineEngineUrl
    }).then((service) => {
        console.debug(`Booted in ${Math.ceil(Date.now() - now)}ms`)
        const chatRoot = document.querySelector<HTMLElement>("#aiaudio-chat-root")
        if (!isDefined(chatRoot)) return
        // Lazy thunk: re-resolves the live project on each agent call so a project
        // open/close after boot doesn't strand the bridge with a stale reference.
        const studioContext = isDefined(service)
            ? makeStudioContext(() => (service.hasProfile ? service : null))
            : undefined
        wireChatSidebar(chatRoot, {
            wssUrl: import.meta.env.VITE_AGENT_WSS_URL ?? "ws://localhost:8000/v1/session/default",
            getJwt,
            ...(isDefined(studioContext) ? {studioContext} : {}),
            getProjectId: () => isDefined(service) && service.hasProfile
                ? UUID.toString(service.profile.uuid)
                : null,
        })
    })
} else {
    alert("crossOriginIsolated must be enabled")
}